'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface StatisticData {
  id: string;
  value: string;
  label: string;
  description: string;
  icon: string;
}

const ProblemStatement = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedStat, setSelectedStat] = useState<string>('hie');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const statistics: StatisticData[] = [];


  const complicationTypes = [
  {
    id: 'hie',
    name: 'Hypoxic-Ischemic Encephalopathy',
    prevalence: '10-15 per 1,000 live births in India',
    mortality: '10-60% mortality rate',
    impact: 'Leading cause of neonatal brain injury in LMICs'
  },
  {
    id: 'seizures',
    name: 'Neonatal Seizures',
    prevalence: '15-20 per 1,000 live births',
    mortality: '15-30% mortality rate',
    impact: 'Associated with poor neurodevelopment without monitoring'
  },
  {
    id: 'stroke',
    name: 'Perinatal Stroke',
    prevalence: '1 in 2,300 live births',
    mortality: '10-20% mortality rate',
    impact: 'Lifelong motor and cognitive deficits'
  }];


  const currentStat = statistics.find((s) => s.id === selectedStat) || statistics[0];

  return (
    <section id="problem-statement" className="relative py-16 overflow-hidden">
      {/* Medical gradient background with soft blues and hospital theme - reduced opacity */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-cyan-50/20 to-blue-100/30 dark:from-blue-950/40 dark:via-cyan-950/30 dark:to-blue-900/40"></div>
      
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `radial-gradient(circle at 20px 20px, rgba(59, 130, 246, 0.1) 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }}></div>
      
      {/* Hospital imagery - subtle background */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10">
        <div className="absolute top-10 right-10 w-96 h-96 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-gradient-to-tr from-blue-300 to-cyan-300 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center space-x-2 bg-error/10 text-error px-4 py-2 rounded-2xl mb-4">
            
            <span className="text-sm font-semibold"></span>
          </div>

          <h2 className="lg:text-4xl font-bold font-heading text-foreground mb-4 text-[32px]">The Problem

          </h2>

          {/* Redesigned Birth Asphyxia Statistics - Prominent & Impactful */}
          <div className="relative bg-gradient-to-br from-error/10 via-error/5 to-error/10 border-2 border-error/30 rounded-3xl p-8 mb-6 max-w-5xl mx-auto shadow-xl overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-error/5 rounded-full blur-3xl -z-0"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-error/5 rounded-full blur-3xl -z-0"></div>
            
            <div className="relative z-10">
              {/* Main headline statistic */}
              <div className="text-center mb-6 pb-6 border-b-2 border-error/20">
                <div className="flex items-center justify-center mb-3">
                  
                </div>
                <div className="text-6xl lg:text-7xl font-black text-error mb-2 tracking-tight">
                  1 Million
                </div>
                <p className="text-xl lg:text-2xl font-bold text-foreground">
                  Newborn Deaths Annually from Birth Asphyxia
                </p>
                <p className="text-sm text-muted-foreground mt-2 font-medium">
                  A preventable tragedy affecting families worldwide
                </p>
              </div>

              {/* Secondary statistics grid */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-card/80 backdrop-blur-sm rounded-2xl p-6 border border-error/20 shadow-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-3 bg-error/20 rounded-xl">
                      <Icon name="ChartBarSquareIcon" size={24} className="text-error" />
                    </div>
                    <div>
                      <div className="text-4xl font-black text-error">Top 3</div>
                      <p className="text-sm font-semibold text-foreground">Global Cause</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Birth Asphyxia ranks among the <strong className="text-foreground">top 3 leading causes</strong> of newborn deaths worldwide
                  </p>
                </div>

                <div className="bg-card/80 backdrop-blur-sm rounded-2xl p-6 border border-error/20 shadow-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-3 bg-error/20 rounded-xl">
                      <Icon name="UserGroupIcon" size={24} className="text-error" />
                    </div>
                    <div>
                      <div className="text-4xl font-black text-error">60%</div>
                      <p className="text-sm font-semibold text-foreground">Survivors at Risk</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Without proper treatment, survivors face <strong className="text-foreground">lifelong disabilities</strong>
                  </p>
                </div>
              </div>

              {/* Long-term disabilities - ENHANCED & PROMINENT */}
              <div className="bg-gradient-to-br from-error/15 via-error/10 to-error/15 rounded-3xl p-8 border-2 border-error/40 shadow-2xl">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-error/20 rounded-2xl mb-4">
                    
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-black text-foreground mb-2">
                    Lifelong Impact on Survivors
                  </h3>
                  <p className="text-base text-muted-foreground font-medium">
                    Without timely intervention, survivors face severe long-term disabilities
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                  { name: 'Cerebral Palsy', icon: 'UserCircleIcon', description: 'Motor function impairment' },
                  { name: 'Mental Retardation', icon: 'AcademicCapIcon', description: 'Cognitive development delays' },
                  { name: 'Seizures', icon: 'BoltIcon', description: 'Recurring neurological episodes' },
                  { name: 'Learning Disability', icon: 'BookOpenIcon', description: 'Educational challenges' }].
                  map((disability) =>
                  <div key={disability.name} className="bg-card rounded-2xl p-5 border-2 border-error/30 shadow-lg hover:shadow-xl transition-shadow">
                      <div className="flex flex-col items-center text-center space-y-3">
                        <div className="p-3 bg-error/10 rounded-xl">
                          <Icon name={disability.icon} size={28} className="text-error" />
                        </div>
                        <div>
                          <h4 className="text-base font-bold text-foreground mb-1">
                            {disability.name}
                          </h4>
                          <p className="text-xs text-muted-foreground leading-snug">
                            {disability.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-8 bg-gradient-to-r from-blue-50 via-blue-100 to-blue-50 dark:from-blue-950 dark:via-blue-900 dark:to-blue-950 rounded-2xl p-6 border-2 border-blue-500 shadow-xl">
                  <p className="md:text-xl text-center font-bold text-blue-900 dark:text-blue-100 text-[28px]">
                    <Icon name="HeartIcon" size={24} className="inline-block text-blue-600 dark:text-blue-400 mr-3 animate-pulse" />
                    Early intervention with therapeutic hypothermia can significantly reduce these outcomes
                  </p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">Traditional therapeutic hypothermia for HIE requires costly combinations of cooling systems and external patient monitors to track vitals—demanding skilled staff, multiple devices in sync, and complex management that most hospitals cannot provide.

          </p>
        </div>

        {/* Only render statistics grid if there are statistics */}
        {statistics.length > 0 &&
        <div className="grid lg:grid-cols-3 gap-4 mb-10">
            {statistics.map((stat) =>
          <button
            key={stat.id}
            onClick={() => setSelectedStat(stat.id)}
            className={`group relative overflow-hidden rounded-2xl p-6 text-left transition-all duration-300 transform hover:scale-105 hover:shadow-2xl ${
            selectedStat === stat.id ?
            'bg-gradient-to-br from-blue-500 via-cyan-500 to-blue-600 text-white shadow-xl scale-105' :
            'bg-gradient-to-br from-blue-100 via-cyan-50 to-blue-100 dark:from-blue-900 dark:via-cyan-900 dark:to-blue-800 hover:from-blue-200 hover:via-cyan-100 hover:to-blue-200 dark:hover:from-blue-800 dark:hover:via-cyan-800 dark:hover:to-blue-700'}`
            }>

                {/* Animated glow effect */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
            selectedStat === stat.id ? 'animate-pulse' : ''}`
            }>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                </div>

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-xl ${
                selectedStat === stat.id ?
                'bg-white/20 backdrop-blur-sm' : 'bg-blue-500/20 dark:bg-blue-400/20'}`
                }>
                      <Icon
                    name={stat.icon}
                    size={28}
                    className={selectedStat === stat.id ? 'text-white' : 'text-blue-600 dark:text-blue-400'} />

                    </div>
                    {selectedStat === stat.id &&
                <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        <span className="text-xs font-semibold text-white">Active</span>
                      </div>
                }
                  </div>

                  <div className={`text-4xl lg:text-5xl font-black mb-2 ${
              selectedStat === stat.id ?
              'text-white' : 'text-blue-600 dark:text-blue-400'}`
              }>
                    {stat.value}
                  </div>

                  <h3 className={`text-lg font-bold mb-2 ${
              selectedStat === stat.id ?
              'text-white' : 'text-foreground'}`
              }>
                    {stat.label}
                  </h3>

                  <p className={`text-sm leading-relaxed ${
              selectedStat === stat.id ?
              'text-white/90' : 'text-muted-foreground'}`
              }>
                    {stat.description}
                  </p>
                </div>

                {/* Bottom accent line */}
                <div className={`absolute bottom-0 left-0 right-0 h-1 transition-all duration-300 ${
            selectedStat === stat.id ?
            'bg-gradient-to-r from-white/0 via-white/50 to-white/0' : 'bg-gradient-to-r from-blue-500/0 via-blue-500/30 to-blue-500/0 opacity-0 group-hover:opacity-100'}`
            }></div>
              </button>
          )}
          </div>
        }

        {/* Understanding Neonatal Brain Complications - Enhanced Tiled Layout */}
        <div className="bg-gradient-to-br from-surface via-card to-surface rounded-3xl p-8 shadow-2xl border border-border/50">
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 bg-error/10 text-error px-4 py-2 rounded-2xl mb-3">
              
              
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold font-heading text-foreground mb-2">
              Understanding Neonatal Brain Complications
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Three major conditions that require immediate intervention and monitoring
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {complicationTypes.map((complication, index) =>
            <div
              key={complication.id}
              className="group relative overflow-hidden bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 dark:from-blue-900/30 dark:via-indigo-900/30 dark:to-purple-900/30 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 flex flex-col"
              style={{ animationDelay: `${index * 100}ms` }}>

                {/* Animated shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                
                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-bl-full"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-indigo-200/20 dark:bg-indigo-800/20 rounded-tr-full"></div>
                
                <div className="relative z-10 p-6 flex flex-col flex-grow">
                  {/* Header with icon */}
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex-1 pr-3">
                      <h4 className="text-lg lg:text-xl font-bold font-heading text-indigo-900 dark:text-indigo-100 leading-tight mb-2 min-h-[3.5rem] flex items-center">
                        {complication.name}
                      </h4>
                      <div className="w-12 h-1 bg-indigo-400/60 dark:bg-indigo-500/60 rounded-full"></div>
                    </div>
                    


                  </div>
                  
                  {/* Stats tiles */}
                  <div className="space-y-3 mb-4">
                    <div className="bg-white/60 dark:bg-indigo-950/40 backdrop-blur-sm rounded-xl p-4 border border-indigo-200/50 dark:border-indigo-700/50 hover:bg-white/80 dark:hover:bg-indigo-950/60 transition-colors">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="ChartBarIcon" size={16} className="text-indigo-600 dark:text-indigo-400" />
                        <span className="text-xs text-indigo-700 dark:text-indigo-300 font-semibold uppercase tracking-wide">Prevalence</span>
                      </div>
                      <span className="block text-base font-bold text-indigo-900 dark:text-indigo-100">{complication.prevalence}</span>
                    </div>
                    
                    <div className="bg-white/60 dark:bg-indigo-950/40 backdrop-blur-sm rounded-xl p-4 border border-indigo-200/50 dark:border-indigo-700/50 hover:bg-white/80 dark:hover:bg-indigo-950/60 transition-colors">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="ExclamationCircleIcon" size={16} className="text-indigo-600 dark:text-indigo-400" />
                        <span className="text-xs text-indigo-700 dark:text-indigo-300 font-semibold uppercase tracking-wide">Mortality</span>
                      </div>
                      <span className="block text-base font-bold text-indigo-900 dark:text-indigo-100">{complication.mortality}</span>
                    </div>
                  </div>
                  
                  {/* Impact section - pushed to bottom */}
                  <div className="pt-4 border-t border-indigo-300/40 dark:border-indigo-700/40 mt-auto">
                    <div className="flex items-start space-x-2">
                      <Icon name="InformationCircleIcon" size={18} className="text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-indigo-800 dark:text-indigo-200 leading-relaxed font-medium">{complication.impact}</p>
                    </div>
                  </div>
                </div>

                {/* Bottom accent bar */}
                <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-indigo-400/0 via-indigo-400/50 to-indigo-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            )}
          </div>
          
          {/* Additional context footer */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center space-x-2 bg-blue-500/10 text-blue-600 dark:text-blue-400 px-6 py-3 rounded-xl">
              <Icon name="LightBulbIcon" size={20} />
              <span className="text-sm font-semibold">Early detection and intervention are critical for positive outcomes</span>
            </div>
          </div>
        </div>
      </div>

      {/* Gradient transition to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent via-blue-50/50 dark:via-blue-950/50 to-white dark:to-gray-950 z-10" />
    </section>);

};

export default ProblemStatement;